import { useParams, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Layout from "../Components/Layout/Layout";
import SEO from "../Components/SEO";
import CodeBlock from "../Components/CodeBlock";
import AlertPopup from "../Components/Slice/AlertPopup";
import blogsData from "../api/blogs.json";
import DefaultPhoto from "../assets/images/Inan Celis with Laptop.png";
import "../assets/styles/blog-post.scss";

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState({ message: '', type: '', isVisible: false });

  // Find blog synchronously so first render matches react-snap pre-rendered HTML
  const cleanSlug = slug?.replace(/\/$/, '') || slug;
  const blog = blogsData.find((b) => b.slug === cleanSlug) || null;

  useEffect(() => {
    if (!blog) {
      navigate("/404");
    }
  }, [blog, navigate]);

  if (!blog) {
    return null;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description: blog.metaDescription,
    author: {
      "@type": "Person",
      name: blog.author,
      url: "https://inancelis.com/",
    },
    datePublished: blog.date,
    dateModified: blog.date,
    publisher: {
      "@type": "Person",
      name: "Inan Celis",
      url: "https://inancelis.com/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://inancelis.com/blog/${blog.slug}/`,
    },
  };

  // Get related posts (exclude current post)
  const relatedPosts = blogsData.filter((b) => b.id !== blog.id).slice(0, 2);

  const goToRelated = (slug) => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.location.href = `/blog/${slug}/`;
    }, 100);
  };

  const redirectPage = (to) => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      const currentId = blog.id;
      if (to === 'prev') {
        const prevBlog = blogsData.find((b) => b.id === currentId + 1);
        if (!prevBlog) {
          setAlert({ message: 'No previous blog post, please click Next.', type: 'error', isVisible: true });
        } else {
          navigate(`/blog/${prevBlog.slug}/`);
          window.location.reload(false);
        }
      } else {
        const nextBlog = blogsData.find((b) => b.id === currentId - 1);
        if (!nextBlog) {
          setAlert({ message: 'No more blog posts, please click Previous.', type: 'error', isVisible: true });
        } else {
          navigate(`/blog/${nextBlog.slug}/`);
          window.location.reload(false);
        }
      }
    }, 1000);
  };

  // Render section based on type
  const renderSection = (section, index) => {
    switch (section.type) {
      case "text":
        return (
          <div key={index} className="blog-section">
            <h2>{section.heading}</h2>
            <p>{section.content}</p>
          </div>
        );

      case "code":
        return (
          <CodeBlock
            key={index}
            code={section.code}
            language={section.language}
            heading={section.heading}
            caption={section.caption}
          />
        );

      case "image":
        // External URLs and root-relative paths are used as-is; a bare
        // filename is resolved against the public /blog-images/ folder.
        const isExternalImage =
          section.src.startsWith("http://") ||
          section.src.startsWith("https://");
        const imageSrc = isExternalImage || section.src.startsWith("/")
          ? section.src
          : `/blog-images/${section.src}`;

        return (
          <div key={index} className="blog-section blog-image-section">
            <div className="blog-image-wrapper">
              <img src={imageSrc} alt={section.alt} loading="lazy" />
              {section.caption && (
                <p className="image-caption text-muted">{section.caption}</p>
              )}
            </div>
          </div>
        );

      case "list":
        return (
          <div key={index} className="blog-section blog-list-section">
            {section.heading && <h2>{section.heading}</h2>}
            {section.intro && <p className="list-intro">{section.intro}</p>}
            {section.listType === "ordered" ? (
              <ol className="blog-ordered-list">
                {section.items.map((item, i) =>
                  typeof item === "string" ? (
                    <li key={i}>{item}</li>
                  ) : (
                    <li key={i}>
                      <strong>{item.title}</strong>
                      {item.description && <p>{item.description}</p>}
                    </li>
                  )
                )}
              </ol>
            ) : section.listType === "checklist" ? (
              <ul className="blog-checklist">
                {section.items.map((item, i) => (
                  <li key={i}>
                    <span className="checklist-icon">
                      <i className="fa-solid fa-check"></i>
                    </span>
                    <div>
                      {typeof item === "string" ? (
                        item
                      ) : (
                        <>
                          <strong>{item.title}</strong>
                          {item.description && <p>{item.description}</p>}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : section.listType === "description" ? (
              <dl className="blog-description-list">
                {section.items.map((item, i) => (
                  <div key={i} className="description-item">
                    <dt>{item.title}</dt>
                    <dd>{item.description}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <ul className="blog-unordered-list">
                {section.items.map((item, i) =>
                  typeof item === "string" ? (
                    <li key={i}>{item}</li>
                  ) : (
                    <li key={i}>
                      <strong>{item.title}</strong>
                      {item.description && <span>: {item.description}</span>}
                    </li>
                  )
                )}
              </ul>
            )}
            {section.outro && <p className="list-outro">{section.outro}</p>}
          </div>
        );

      case "faq":
        return (
          <div key={index} className="blog-section blog-faq-section">
            {section.heading && <h2>{section.heading}</h2>}
            <div className="faq-list">
              {section.items.map((item, i) => (
                <div key={i} className="faq-item">
                  <div className="faq-question">
                    <span className="faq-badge">Q</span>
                    <h3>{item.question}</h3>
                  </div>
                  <div className="faq-answer">
                    <span className="faq-badge">A</span>
                    <p>{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <AlertPopup
        alertInfo={alert}
        onClose={() => setAlert({ ...alert, isVisible: false })}
      />
      <div className="paginator shadow">
        <button type="button" onClick={() => redirectPage('prev')} title="Previous">
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <button type="button" onClick={() => redirectPage('next')} title="Next">
          <i className="fa-solid fa-arrow-right"></i>
        </button>
      </div>
      <SEO
        title={blog.title}
        description={blog.metaDescription}
        url={`https://inancelis.com/blog/${blog.slug}/`}
        image={
          blog.og_image ||
          `https://inancelis.com${DefaultPhoto}`
        }
        type="article"
        structuredData={structuredData}
        breadcrumbs={[
          { name: "Home", url: "https://inancelis.com/" },
          { name: "Blog", url: "https://inancelis.com/blog/" },
          { name: blog.title, url: `https://inancelis.com/blog/${blog.slug}/` },
        ]}
      />
      <article className="container blog-post-page py-5 pt-5">
        <div className="blog-post-header" data-aos="fade-up">
          <NavLink to="/blog/" className="back-link">
            <i className="fa-solid fa-arrow-left"></i> Back to Blog
          </NavLink>
          <div className="blog-meta">
            <span className="blog-category">{blog.category}</span>
            <span className="blog-date">{blog.date}</span>
          </div>
          <h1 className="blog-post-title">{blog.title}</h1>
          <div className="blog-post-info">
            <span className="blog-author">
              <i className="fa-regular fa-user"></i> <NavLink to="/">{blog.author}</NavLink>
            </span>
            <span className="blog-read-time">
              <i className="fa-regular fa-clock"></i> {blog.readTime}
            </span>
          </div>
        </div>

        <div className="blog-post-content" data-aos="fade-up">
          <div className="blog-introduction">
            <p>{blog.content.introduction}</p>
          </div>

          {blog.content.sections.map((section, index) =>
            renderSection(section, index)
          )}

          <div className="blog-conclusion">
            <h2>Conclusion</h2>
            <p>{blog.content.conclusion}</p>
          </div>
        </div>

        <div className="blog-post-footer" data-aos="fade-up">
          <div className="blog-tags">
            <strong>Tags:</strong>
            {blog.tags.map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>

          <div className="blog-share">
            <strong>Share this article:</strong>
            <div className="share-buttons">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  blog.title
                )}&url=${encodeURIComponent(
                  `https://inancelis.com/blog/${blog.slug}/`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn twitter"
              >
                <i className="fa-brands fa-twitter"></i>
              </a>
              <a
                href={`https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  `https://inancelis.com/blog/${blog.slug}/`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn linkedin"
              >
                <i className="fa-brands fa-linkedin"></i>
              </a>
              <a
                href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  `https://inancelis.com/blog/${blog.slug}/`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn facebook"
              >
                <i className="fa-brands fa-facebook"></i>
              </a>
            </div>
          </div>
        </div>

        {relatedPosts.length > 0 && (
          <div className="related-posts" data-aos="fade-up">
            <h3>Related Articles</h3>
            <div className="related-posts-grid">
              {relatedPosts.map((relatedBlog) => (
                <div key={relatedBlog.id} className="related-post-card">
                  <div className="related-post-meta">
                    <span className="blog-category">
                      {relatedBlog.category}
                    </span>
                    <span className="blog-date">{relatedBlog.date}</span>
                  </div>
                  <h4>
                    <a href={`/blog/${relatedBlog.slug}/`} onClick={(e) => { e.preventDefault(); goToRelated(relatedBlog.slug); }}>
                      {relatedBlog.title}
                    </a>
                  </h4>
                  <p className="text-muted">{relatedBlog.excerpt}</p>
                  <a
                    href={`/blog/${relatedBlog.slug}/`}
                    className="read-more"
                    onClick={(e) => { e.preventDefault(); goToRelated(relatedBlog.slug); }}
                  >
                    Read More <i className="fa-solid fa-arrow-right"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="blog-cta" data-aos="fade-up">
          <h3>Need a Web Developer for Your Project?</h3>
          <p className="text-muted">
            Looking for a professional web developer in the Philippines? Let's
            discuss how I can help bring your project to life with modern
            technologies and best practices.
          </p>
          <NavLink to="/contact/" className="cta-button">
            <i className="fa-regular fa-paper-plane"></i> Get In Touch
          </NavLink>
        </div>
      </article>
    </Layout>
  );
}

export default BlogPost;
